import ArchitectureTabHeader from "./architecture-tab-header";
import { useState, useEffect, useCallback, useRef } from "react";
import { TabsType } from "@/types/diagram";
import IsometricAgentLayout from "./isometric-agent-layout";
import { useDiagram } from "@/context/diagram-context";
import { usePanel } from "@/context/panel-context";
import { usePanelProps } from "@/context/panel-props-context";
import CompositionTab from "./composition-tab";
import ShapesTab from "./shapes-tab";
import DiagramTab from "./diagram-tab";
import AttachmentOptionsPanel from "./attachment-options-panel";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useGenerateFlowService from "@/service-hooks/use-generate-flow-service";
import { compileDiagram } from "@/lib/diagram-components.lib";
import { shapesLibraryManager } from "@/lib/shapes-manager";
import { useUI } from "@/context/ui-context";
import { useLibrary } from "@/context/library-context";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/lib/constants";

const ArchitectureAgentLayout = ({ Chat: _Chat }: { Chat: React.ReactNode }) => {
  const [activeTab, setActiveTab] = useState<TabsType>("diagrams");
  const diagram = useDiagram();
  const { setPanelType } = usePanel();
  const { setPanelProps } = usePanelProps();
  const { id: uuid = "" } = useParams();
  const { showAttachmentPoints, canvasSettings } = useUI();
  const { handleMissingDependencies, ensureLibrary } = useLibrary();
  // the canvas shows the shape library: load it now
  useEffect(() => {
    ensureLibrary();
  }, [ensureLibrary]);

  // Handle save shortcut - switch to diagrams tab and trigger save
  const handleSaveShortcut = useCallback(() => {
    setActiveTab("diagrams");
    // Dispatch custom event to trigger save modal in DiagramTab
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("openSaveDiagramModal"));
    }, 100);
  }, []);

  // Generate flow service for blueprint generation
  const { mutate, abort } = useGenerateFlowService(async (res, error) => {
    // Always stop loading state
    diagram.setIsDiagramLoading(false);

    // Handle error cases
    if (error) {
      // Don't show error toast for user-cancelled requests
      if (error.name !== "AbortError") {
        toast.error(error.message || "Failed to generate blueprint", {
          duration: TOAST_DURATION,
        });
      }
      return;
    }

    // Handle success case
    if (res?.metadata?.result && res?.metadata?.result?.length > 0) {
      const diagramComponents = res.metadata.result;
      await handleMissingDependencies(diagramComponents);
      const { svgContent, processedComponents } = compileDiagram(
        diagramComponents,
        diagram.canvasSize,
        shapesLibraryManager.getAllShapes(),
        showAttachmentPoints
      );
      diagram.setDiagramComponents(processedComponents);
      diagram.setComposedSVG(svgContent);
      diagram.selectShape(null);
    }
  });

  // Memoized handler for blueprint generation from unified model
  const handleGenerateBlueprint = useCallback(() => {
    diagram.setIsDiagramLoading(true);
    mutate({ key: "blueprint", uuid });
  }, [uuid, mutate, diagram]);

  // Memoized handler to abort generation
  const handleAbortGeneration = useCallback(() => {
    abort();
    diagram.setIsDiagramLoading(false);
    toast.info("Blueprint generation cancelled", {
      duration: TOAST_DURATION,
    });
  }, [abort, diagram]);

  // Opening this page with router state `{ generateBlueprint: true }` (Architecture modeler on the dashboard,
  // the ontology explorer's play button, the side panel) generates the blueprint once the page is mounted.
  // Before, the launchers navigated and then fired the event below on a 500 ms timer; when the page took
  // longer to mount the event was lost and nothing rendered (BREEZEAI-1471 BUG-15). The state is cleared so a
  // reload or back-navigation doesn't regenerate, and the location key guards against a second run.
  const location = useLocation();
  const navigate = useNavigate();
  const blueprintHandledFor = useRef<string | null>(null);
  const blueprintRequested = (location.state as { generateBlueprint?: unknown } | null)?.generateBlueprint === true;
  useEffect(() => {
    if (!blueprintRequested || blueprintHandledFor.current === location.key) return;
    blueprintHandledFor.current = location.key;
    navigate(location.pathname, { replace: true, state: null });
    handleGenerateBlueprint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprintRequested, location.key]);

  // Listen for the custom event to trigger blueprint generation from unified model (used when the page is
  // already open)
  useEffect(() => {
    window.addEventListener(
      "generateBlueprintFromUnifiedModel",
      handleGenerateBlueprint
    );

    return () => {
      window.removeEventListener(
        "generateBlueprintFromUnifiedModel",
        handleGenerateBlueprint
      );
    };
  }, [handleGenerateBlueprint]);

  // Listen for abort generation event
  useEffect(() => {
    window.addEventListener("abortBlueprintGeneration", handleAbortGeneration);

    return () => {
      window.removeEventListener(
        "abortBlueprintGeneration",
        handleAbortGeneration
      );
    };
  }, [handleAbortGeneration]);

  return (
    <div className="flex flx-row h-full bg-transparent">
      <div className="w-2/6 bg-surface-dark">
        <div className="flex flex-col h-full p-2 min-h-0">
          <ArchitectureTabHeader tabControls={{ activeTab, setActiveTab }} />
          {activeTab === "shapes" && <ShapesTab />}
          {activeTab === "composition" && <CompositionTab />}
          {activeTab === "diagrams" && <DiagramTab />}
        </div>
      </div>
      <div className="w-full p-2 border-l border-t border-surface-border bg-[#FFFFFF] dark:bg-surface-dark">
        <AttachmentOptionsPanel />
        <IsometricAgentLayout
          settings={canvasSettings}
          handleComponentMetadata={(
            panel: boolean,
            metadata: Record<string, any>
          ) => {
            if (panel) {
              setPanelProps({ mode: "METADATA_VIEWER", metadata });
              setPanelType("METADATA_VIEWER");
            }
          }}
          onSaveShortcut={handleSaveShortcut}
          selected3DShape={diagram.selectedShape}
          canvasSize={diagram.canvasSize}
          setSelectedPosition={diagram.setSelectedPosition}
          setSelectedAttachmentPoint={(point: string) =>
            diagram.setSelectedAttachmentPoint(point)
          }
        />
      </div>
    </div>
  );
};

export default ArchitectureAgentLayout;
